import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { usePrestamos } from "../hooks/usePrestamos";
import { useCuentas } from "../../../settings/hooks/useSettings";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import ConfirmModal from "../../shared/ConfirmModal";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import styles from "./Prestamos.module.css";

const MONEDAS = ["CRC", "USD"];
const MONEDA_SYMBOL = { CRC: "₡", USD: "$" };
const EMPTY_PRESTAMO = {
  nombre: "", entidad_financiera: "", monto_inicial: "", moneda: "CRC",
  tipo_cambio: "1", tasa_interes: "", fecha_inicio: "", fecha_vencimiento: "",
  cuota_mensual: "", cuenta_bancaria: "",
};
const EMPTY_PAGO = { fecha_pago: "", monto_capital: "", monto_interes: "", documento: "" };

/** "2026-01-15" → "15/01/2026" (tolera undefined/inválido) */
function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

// ─────────────────────────────────────────────────────────
// Estilos compartidos por los modales (mismo patrón que EmitirNotaCreditoModal.js)
// ─────────────────────────────────────────────────────────
const OVERLAY = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  zIndex: 1000, backdropFilter: "blur(3px)",
};
const MODAL_BASE = {
  position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
  background: "var(--bg-card)", border: "1px solid var(--border-card)",
  borderRadius: 12, padding: "24px 24px 20px", zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)", maxHeight: "90vh", overflowY: "auto",
};
const LABEL_STYLE = {
  display: "block", color: "var(--text-muted)", fontSize: "0.7rem", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4,
};
const INPUT_STYLE = {
  width: "100%", padding: "8px 10px", background: "var(--bg-input)",
  border: "1px solid var(--border-card)", borderRadius: 6, color: "var(--text-primary)",
  fontSize: "0.85rem", outline: "none", boxSizing: "border-box", colorScheme: "light dark",
};

function Divider() {
  return <div style={{ borderTop: "1px solid var(--border-card)", margin: "16px 0" }} />;
}
function CloseBtn({ onClick }) {
  return (
    <button onClick={onClick} style={{
      background: "none", border: "none", color: "var(--text-muted)",
      cursor: "pointer", fontSize: "1.15rem", lineHeight: 1, padding: "2px 4px",
    }}>✕</button>
  );
}
function Field({ label, required, children }) {
  return (
    <div>
      <label style={LABEL_STYLE}>{label}{required && <span style={{ color: "#f87171", marginLeft: 3 }}>*</span>}</label>
      {children}
    </div>
  );
}
function PrimaryBtn({ children, onClick, disabled, color = "#f97316" }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "8px 18px", background: disabled ? `${color}66` : color, color: "#fff",
      border: "none", borderRadius: 7, fontSize: "0.84rem", fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer", display: "flex",
      alignItems: "center", gap: 7,
    }}>{children}</button>
  );
}
function GhostBtn({ children, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "8px 16px", background: "transparent", border: "1px solid var(--border-card)",
      borderRadius: 7, color: "var(--text-muted)", fontSize: "0.84rem",
      cursor: disabled ? "not-allowed" : "pointer",
    }}>{children}</button>
  );
}

function ModalShell({ icon, title, subtitle, onClose, children, width = 560 }) {
  return (
    <>
      <div onClick={onClose} style={OVERLAY} />
      <div style={{ ...MODAL_BASE, width: `min(${width}px, 94vw)` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ background: "rgba(249,115,22,0.15)", borderRadius: 8, padding: "8px 10px", color: "#f97316" }}>
              <i className={`fa-solid ${icon}`} style={{ fontSize: "1.1rem" }} />
            </div>
            <div>
              <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>{title}</h3>
              {subtitle && <p style={{ margin: "2px 0 0", color: "var(--text-muted)", fontSize: "0.78rem" }}>{subtitle}</p>}
            </div>
          </div>
          <CloseBtn onClick={onClose} />
        </div>
        <Divider />
        {children}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
// Stat card (mismo lenguaje visual que el Dashboard)
// ─────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon, iconBg, iconColor }) {
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-card)",
      borderRadius: 14, padding: "16px 18px", display: "flex",
      alignItems: "flex-start", justifyContent: "space-between", gap: 8,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.7px",
          textTransform: "uppercase", color: "var(--text-muted)" }}>{label}</p>
        <p style={{ margin: "5px 0 2px", fontFamily: "monospace", fontWeight: 800,
          fontSize: "1.3rem", color: "var(--text-primary)", lineHeight: 1.1,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</p>
        {sub && <p style={{ margin: 0, fontSize: "0.67rem", color: "var(--text-muted)" }}>{sub}</p>}
      </div>
      <div style={{ width: 42, height: 42, borderRadius: 11, flexShrink: 0, background: iconBg,
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <i className={`fa-solid ${icon}`} style={{ color: iconColor, fontSize: "1.05rem" }} />
      </div>
    </div>
  );
}

function Badge({ children, color }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px",
      borderRadius: 20, fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase",
      letterSpacing: "0.3px", color, background: `${color}18`, border: `1px solid ${color}44`,
    }}>{children}</span>
  );
}

// ─────────────────────────────────────────────────────────
// Card de un préstamo individual
// ─────────────────────────────────────────────────────────
function PrestamoCard({ p, onVerPagos, onPago, onEditar, onEliminar }) {
  const sym = MONEDA_SYMBOL[p.moneda] ?? p.moneda;
  const saldo = Number(p.saldo ?? p.monto_inicial);
  const inicial = Number(p.monto_inicial) || 1;
  const pctPagado = Math.min(100, Math.max(0, ((inicial - saldo) / inicial) * 100));
  const vencido = p.activo && p.fecha_vencimiento && new Date(p.fecha_vencimiento) < new Date();

  return (
    <div className={styles.prestamoCard}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
        <div style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0, background: "rgba(249,115,22,0.14)",
          border: "1px solid rgba(249,115,22,0.28)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <i className="fa-solid fa-hand-holding-dollar" style={{ color: "#f97316", fontSize: "1rem" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, color: "var(--text-primary)", fontSize: "0.92rem" }}>{p.nombre}</span>
            {!p.activo ? (
              <Badge color="var(--text-muted)">Inactivo</Badge>
            ) : vencido ? (
              <Badge color="#f87171">Vencido</Badge>
            ) : (
              <Badge color="#4ade80">Activo</Badge>
            )}
          </div>
          {p.entidad_financiera && (
            <p style={{ margin: "2px 0 0", color: "var(--text-muted)", fontSize: "0.78rem" }}>{p.entidad_financiera}</p>
          )}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Saldo actual
        </span>
        <span style={{ fontFamily: "monospace", fontWeight: 800, fontSize: "1.15rem", color: "var(--text-primary)" }}>
          {sym}{formatCurrency(saldo)}
        </span>
      </div>
      <div className={styles.progressTrack}>
        <div className={styles.progressFill} style={{ width: `${pctPagado}%` }} />
      </div>
      <p style={{ margin: "4px 0 14px", fontSize: "0.72rem", color: "var(--text-muted)", textAlign: "right" }}>
        {pctPagado.toFixed(1)}% pagado
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px", marginBottom: 16,
        fontSize: "0.78rem", color: "var(--text-secondary)" }}>
        <div><span style={{ color: "var(--text-muted)" }}>Tasa: </span>{Number(p.tasa_interes).toFixed(2)}%</div>
        <div><span style={{ color: "var(--text-muted)" }}>Cuota: </span>{sym}{formatCurrency(p.cuota_mensual)}</div>
        <div style={{ gridColumn: "span 2" }}>
          <span style={{ color: "var(--text-muted)" }}>Vence: </span>{fmtDate(p.fecha_vencimiento)}
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {actionBtn(onVerPagos, "rgba(96,165,250,0.1)", "rgba(96,165,250,0.3)", "#60a5fa", "fa-clock-rotate-left", "Ver pagos")}
        {actionBtn(onPago, "rgba(74,222,128,0.1)", "rgba(74,222,128,0.3)", "#4ade80", "fa-money-check-dollar", "Registrar pago")}
        {actionBtn(onEditar, "rgba(249,115,22,0.1)", "rgba(249,115,22,0.3)", "#f97316", "fa-pencil", "Editar")}
        {actionBtn(onEliminar, "rgba(239,68,68,0.1)", "rgba(239,68,68,0.25)", "#f87171", "fa-trash", "Eliminar")}
      </div>
    </div>
  );
}

function actionBtn(onClick, bg, border, color, icon, title) {
  return (
    <button type="button" onClick={onClick} title={title} style={{
      background: bg, border: `1px solid ${border}`, borderRadius: 6, color,
      width: 30, height: 30, display: "inline-flex", alignItems: "center",
      justifyContent: "center", cursor: "pointer", fontSize: "0.8rem",
    }}>
      <i className={`fa-solid ${icon}`} />
    </button>
  );
}

// ─────────────────────────────────────────────────────────
// Página principal
// ─────────────────────────────────────────────────────────
export default function ListPrestamos() {
  const { prestamos, loading, create, update, remove, registrarPago, eliminarPago } = usePrestamos();
  const { cuentas } = useCuentas();

  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_PRESTAMO);
  const [saving, setSaving] = useState(false);

  const [editando, setEditando] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_PRESTAMO);

  const [pagando, setPagando] = useState(null);
  const [pagoForm, setPagoForm] = useState(EMPTY_PAGO);
  const [pagoSaving, setPagoSaving] = useState(false);

  const [viendoPagos, setViendoPagos] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [confirmandoPago, setConfirmandoPago] = useState(null);
  const [deletingPago, setDeletingPago] = useState(false);

  const [actionMsg, setActionMsg] = useState(null);

  // Mantiene el modal "Historial de Pagos" sincronizado tras eliminar un pago
  // (usePrestamos recarga la lista completa; viendoPagos guarda una referencia
  // puntual que hay que refrescar con el objeto actualizado).
  useEffect(() => {
    if (!viendoPagos) return;
    const actualizado = prestamos.find((p) => p.id === viendoPagos.id);
    if (actualizado && actualizado !== viendoPagos) setViendoPagos(actualizado);
    if (!actualizado) setViendoPagos(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prestamos]);

  function showMsg(ok, text) {
    setActionMsg({ ok, text });
    setTimeout(() => setActionMsg(null), 4000);
  }

  async function handleCrear() {
    setSaving(true);
    try {
      await create(form);
      setForm(EMPTY_PRESTAMO);
      setNuevoOpen(false);
      showMsg(true, "Préstamo creado correctamente.");
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleEditar() {
    setSaving(true);
    try {
      await update(editando.id, editForm);
      setEditando(null);
      showMsg(true, "Préstamo actualizado correctamente.");
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handlePago() {
    setPagoSaving(true);
    try {
      await registrarPago(pagando.id, pagoForm);
      setPagando(null);
      showMsg(true, "Pago registrado correctamente.");
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setPagoSaving(false);
    }
  }

  async function handleEliminar() {
    setDeleting(true);
    try {
      await remove(confirmando.id);
      setConfirmando(null);
      showMsg(true, "Préstamo eliminado correctamente.");
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setDeleting(false);
    }
  }

  async function handleEliminarPago() {
    setDeletingPago(true);
    try {
      await eliminarPago(confirmandoPago.id);
      setConfirmandoPago(null);
      showMsg(true, "Pago eliminado correctamente.");
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setDeletingPago(false);
    }
  }

  // ── Stats ────────────────────────────────────────────────────────────────
  const activos = prestamos.filter((p) => p.activo);
  const deudaTotal = activos.reduce((acc, p) => acc + Number(p.saldo ?? p.monto_inicial), 0);
  const pagadoTotal = activos.reduce((acc, p) => acc + (Number(p.monto_inicial) - Number(p.saldo ?? p.monto_inicial)), 0);
  const proximoVencimiento = activos
    .map((p) => p.fecha_vencimiento)
    .filter(Boolean)
    .sort()[0];

  return (
    <div>
      <span className={invoiceStyles.inactive}>
        <Link to="/"><span className={invoiceStyles.inactive}>Inicio /</span></Link>
        <span className={invoiceStyles.active}> Préstamos</span>
      </span>
      <h2 className="mt-2 me-2 p-0 title">Préstamos</h2>
      <p className="mb-3" style={{ color: "var(--text-muted)" }}>
        Deuda bancaria — préstamos y leasings vigentes, con su saldo y pagos de cuota.
      </p>

      <div className={styles.statsGrid}>
        <StatCard label="Deuda Total" value={`₡${formatCurrency(deudaTotal)}`}
          sub={`${activos.length} préstamo(s) activo(s)`}
          icon="fa-building-columns" iconBg="rgba(239,68,68,0.2)" iconColor="#ef4444" />
        <StatCard label="Total Pagado" value={`₡${formatCurrency(pagadoTotal)}`}
          sub="Capital amortizado"
          icon="fa-wallet" iconBg="rgba(74,222,128,0.2)" iconColor="#4ade80" />
        <StatCard label="Préstamos Activos" value={activos.length}
          sub={`${prestamos.length} en total`}
          icon="fa-hand-holding-dollar" iconBg="rgba(139,92,246,0.2)" iconColor="#8b5cf6" />
        <StatCard label="Próximo Vencimiento" value={proximoVencimiento ? fmtDate(proximoVencimiento) : "—"}
          sub="Entre los préstamos activos"
          icon="fa-calendar-day" iconBg="rgba(234,179,8,0.2)" iconColor="#eab308" />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <PrimaryBtn onClick={() => { setForm(EMPTY_PRESTAMO); setNuevoOpen(true); }}>
          <i className="fa-solid fa-plus" />Nuevo préstamo
        </PrimaryBtn>
      </div>

      {loading ? (
        <div className={styles.emptyState}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "#f97316", marginRight: 8 }} />
          Cargando…
        </div>
      ) : prestamos.length === 0 ? (
        <div className={styles.emptyState}>
          <i className="fa-solid fa-hand-holding-dollar" style={{ fontSize: "2.4rem", display: "block", marginBottom: 12, opacity: 0.25 }} />
          <p style={{ margin: 0, fontSize: "0.88rem" }}>No hay préstamos registrados.</p>
        </div>
      ) : (
        <div className={styles.cardGrid}>
          {prestamos.map((p) => (
            <PrestamoCard
              key={p.id}
              p={p}
              onVerPagos={() => setViendoPagos(p)}
              onPago={() => { setPagoForm(EMPTY_PAGO); setPagando(p); }}
              onEditar={() => {
                setEditForm({
                  nombre: p.nombre, entidad_financiera: p.entidad_financiera || "",
                  tasa_interes: p.tasa_interes, cuota_mensual: p.cuota_mensual,
                  fecha_vencimiento: p.fecha_vencimiento,
                });
                setEditando(p);
              }}
              onEliminar={() => setConfirmando(p)}
            />
          ))}
        </div>
      )}

      {/* ── Modal: Nuevo préstamo ─────────────────────────────────────────── */}
      {nuevoOpen && (
        <ModalShell icon="fa-hand-holding-dollar" title="Nuevo Préstamo"
          subtitle="Registra un préstamo o leasing con una entidad financiera"
          onClose={() => setNuevoOpen(false)} width={620}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px", marginBottom: 18 }}>
            <Field label="Nombre" required>
              <input style={INPUT_STYLE} placeholder="Ej: Operación 1" value={form.nombre}
                onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} />
            </Field>
            <Field label="Entidad Financiera">
              <input style={INPUT_STYLE} placeholder="Ej: Banco Nacional" value={form.entidad_financiera}
                onChange={(e) => setForm((f) => ({ ...f, entidad_financiera: e.target.value }))} />
            </Field>
            <Field label="Cuenta Bancaria" required>
              <select style={{ ...INPUT_STYLE, cursor: "pointer" }} value={form.cuenta_bancaria}
                onChange={(e) => setForm((f) => ({ ...f, cuenta_bancaria: e.target.value }))}>
                <option value="">Seleccione…</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco} — {c.numero_cuenta}</option>)}
              </select>
            </Field>
            <Field label="Monto Inicial" required>
              <input style={INPUT_STYLE} type="number" value={form.monto_inicial}
                onChange={(e) => setForm((f) => ({ ...f, monto_inicial: e.target.value }))} />
            </Field>
            <Field label="Moneda">
              <select style={{ ...INPUT_STYLE, cursor: "pointer" }} value={form.moneda}
                onChange={(e) => setForm((f) => ({ ...f, moneda: e.target.value }))}>
                {MONEDAS.map((m) => <option key={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Tasa Interés Anual (%)" required>
              <input style={INPUT_STYLE} type="number" step="0.001" value={form.tasa_interes}
                onChange={(e) => setForm((f) => ({ ...f, tasa_interes: e.target.value }))} />
            </Field>
            <Field label="Cuota Mensual" required>
              <input style={INPUT_STYLE} type="number" value={form.cuota_mensual}
                onChange={(e) => setForm((f) => ({ ...f, cuota_mensual: e.target.value }))} />
            </Field>
            <Field label="Fecha Inicio" required>
              <input style={INPUT_STYLE} type="date" value={form.fecha_inicio}
                onChange={(e) => setForm((f) => ({ ...f, fecha_inicio: e.target.value }))} />
            </Field>
            <Field label="Fecha Vencimiento" required>
              <input style={INPUT_STYLE} type="date" value={form.fecha_vencimiento}
                onChange={(e) => setForm((f) => ({ ...f, fecha_vencimiento: e.target.value }))} />
            </Field>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <GhostBtn onClick={() => setNuevoOpen(false)} disabled={saving}>Cancelar</GhostBtn>
            <PrimaryBtn onClick={handleCrear} disabled={saving}>
              {saving ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando…</> : <><i className="fa-solid fa-floppy-disk" />Guardar préstamo</>}
            </PrimaryBtn>
          </div>
        </ModalShell>
      )}

      {/* ── Modal: Editar préstamo ────────────────────────────────────────── */}
      {editando && (
        <ModalShell icon="fa-pencil" title="Editar Préstamo" subtitle={editando.nombre}
          onClose={() => setEditando(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px", marginBottom: 18 }}>
            <Field label="Nombre">
              <input style={INPUT_STYLE} value={editForm.nombre}
                onChange={(e) => setEditForm((f) => ({ ...f, nombre: e.target.value }))} />
            </Field>
            <Field label="Entidad Financiera">
              <input style={INPUT_STYLE} value={editForm.entidad_financiera || ""}
                onChange={(e) => setEditForm((f) => ({ ...f, entidad_financiera: e.target.value }))} />
            </Field>
            <Field label="Tasa Interés (%)">
              <input style={INPUT_STYLE} type="number" step="0.001" value={editForm.tasa_interes}
                onChange={(e) => setEditForm((f) => ({ ...f, tasa_interes: e.target.value }))} />
            </Field>
            <Field label="Cuota Mensual">
              <input style={INPUT_STYLE} type="number" value={editForm.cuota_mensual}
                onChange={(e) => setEditForm((f) => ({ ...f, cuota_mensual: e.target.value }))} />
            </Field>
            <Field label="Fecha Vencimiento">
              <input style={INPUT_STYLE} type="date" value={editForm.fecha_vencimiento}
                onChange={(e) => setEditForm((f) => ({ ...f, fecha_vencimiento: e.target.value }))} />
            </Field>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <GhostBtn onClick={() => setEditando(null)} disabled={saving}>Cancelar</GhostBtn>
            <PrimaryBtn onClick={handleEditar} disabled={saving}>
              {saving ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando…</> : <><i className="fa-solid fa-floppy-disk" />Guardar</>}
            </PrimaryBtn>
          </div>
        </ModalShell>
      )}

      {/* ── Modal: Registrar pago ─────────────────────────────────────────── */}
      {pagando && (
        <ModalShell icon="fa-money-check-dollar" title="Registrar Pago de Cuota" subtitle={pagando.nombre}
          onClose={() => setPagando(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px", marginBottom: 18 }}>
            <Field label="Fecha" required>
              <input style={INPUT_STYLE} type="date" value={pagoForm.fecha_pago}
                onChange={(e) => setPagoForm((f) => ({ ...f, fecha_pago: e.target.value }))} />
            </Field>
            <Field label="Documento">
              <input style={INPUT_STYLE} value={pagoForm.documento}
                onChange={(e) => setPagoForm((f) => ({ ...f, documento: e.target.value }))} />
            </Field>
            <Field label="Monto Capital" required>
              <input style={INPUT_STYLE} type="number" value={pagoForm.monto_capital}
                onChange={(e) => setPagoForm((f) => ({ ...f, monto_capital: e.target.value }))} />
            </Field>
            <Field label="Monto Interés" required>
              <input style={INPUT_STYLE} type="number" value={pagoForm.monto_interes}
                onChange={(e) => setPagoForm((f) => ({ ...f, monto_interes: e.target.value }))} />
            </Field>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <GhostBtn onClick={() => setPagando(null)} disabled={pagoSaving}>Cancelar</GhostBtn>
            <PrimaryBtn onClick={handlePago} disabled={pagoSaving} color="#4ade80">
              {pagoSaving ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando…</> : <><i className="fa-solid fa-floppy-disk" />Guardar pago</>}
            </PrimaryBtn>
          </div>
        </ModalShell>
      )}

      {/* ── Modal: Historial de pagos ─────────────────────────────────────── */}
      {viendoPagos && (
        <ModalShell icon="fa-clock-rotate-left" title="Historial de Pagos" subtitle={viendoPagos.nombre}
          onClose={() => setViendoPagos(null)} width={640}>
          {!viendoPagos.pagos?.length ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-muted)", fontSize: "0.85rem" }}>
              Aún no hay pagos registrados para este préstamo.
            </div>
          ) : (
            <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
              <table>
                <thead>
                  <tr>
                    <th>N° Pago</th>
                    <th>Fecha</th>
                    <th>Capital</th>
                    <th>Interés</th>
                    <th>Documento</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {viendoPagos.pagos.map((pago) => (
                    <tr key={pago.id}>
                      <td>{pago.numero_pago}</td>
                      <td>{fmtDate(pago.fecha_pago)}</td>
                      <td>{formatCurrency(pago.monto_capital)}</td>
                      <td>{formatCurrency(pago.monto_interes)}</td>
                      <td>{pago.documento || "—"}</td>
                      <td className="text-center">
                        {actionBtn(() => setConfirmandoPago(pago), "rgba(239,68,68,0.1)", "rgba(239,68,68,0.25)", "#f87171", "fa-trash", "Eliminar pago")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
            <GhostBtn onClick={() => setViendoPagos(null)}>Cerrar</GhostBtn>
          </div>
        </ModalShell>
      )}

      {/* ── Confirmar eliminación ─────────────────────────────────────────── */}
      {confirmando && (
        <ConfirmModal
          title="Eliminar Préstamo"
          message={`¿Estás seguro de que deseas eliminar el préstamo "${confirmando.nombre}"? Esta acción no se puede deshacer. Si tiene pagos registrados, quedará bloqueado.`}
          loading={deleting}
          onConfirm={handleEliminar}
          onCancel={() => setConfirmando(null)}
        />
      )}

      {/* ── Confirmar eliminación de un pago ──────────────────────────────── */}
      {confirmandoPago && (
        <ConfirmModal
          title="Eliminar Pago"
          message={`¿Estás seguro de que deseas eliminar el pago "${confirmandoPago.numero_pago}"? Esto revertirá el asiento contable y el movimiento bancario asociados (si existen). Esta acción no se puede deshacer.`}
          loading={deletingPago}
          onConfirm={handleEliminarPago}
          onCancel={() => setConfirmandoPago(null)}
        />
      )}

      {/* ── Toast flotante ────────────────────────────────────────────────── */}
      {actionMsg && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: actionMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(251,146,60,0.07)",
          border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(251,146,60,0.3)"}`,
          borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#fb923c"}`,
          borderRadius: 8, padding: "12px 18px", display: "flex", alignItems: "center",
          gap: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.4)", maxWidth: 380,
        }}>
          <i className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-triangle-exclamation"}`}
            style={{ color: actionMsg.ok ? "#4ade80" : "#fb923c", fontSize: "1.1rem" }} />
          <span style={{ color: actionMsg.ok ? "#4ade80" : "#fb923c", fontWeight: 600, fontSize: "0.88rem" }}>
            {actionMsg.text}
          </span>
        </div>
      )}
    </div>
  );
}
